import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { requestDiscovery, useHrmsDuplicates, useHrmsSyncStatus, useSyncSchedules } from '@/api/hrms-sync';
import { DuplicatesDialog } from './DuplicatesDialog';
import { HrmsSyncDialog } from './HrmsSyncDialog';
import { SyncScheduleDialog } from './SyncScheduleDialog';
import { fmtWhen } from './format';
import { RunStatusChip } from './shared';

/**
 * Dashboard entry point for the uKnowva data sync: connection state, the last
 * run's outcome, what still needs a mapping — and the button that opens the
 * sync window.
 */
export function HrmsSyncCard({ canEdit, compact = false }: { canEdit: boolean; compact?: boolean }) {
  const { data, isLoading, error } = useHrmsSyncStatus(true);
  const schedules = useSyncSchedules(!!data?.connected);
  const daily = schedules.data?.schedules[0];
  const [open, setOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [followRun, setFollowRun] = useState<string | null>(null);
  const [dupesOpen, setDupesOpen] = useState(false);
  const dupes = useHrmsDuplicates(!!data?.connected);
  const dupeCount = (dupes.data?.groups.length ?? 0) + (dupes.data?.conflicts.length ?? 0);

  // A workspace that cannot reach this endpoint (older backend) shows nothing
  // rather than an error card on the landing page. A standalone workspace has
  // nothing to sync, so no card either.
  if (error || data?.standalone) return null;

  const last = data?.last_run;
  const running = !!data?.active_run_id;

  return (
    <Paper sx={{ p: compact ? '12px 14px' : 2.5, border: 1, borderColor: running ? 'info.main' : 'divider', mb: compact ? 0 : 2, ...(compact && { borderRadius: '12px', boxShadow: 'none', height: '100%', display: 'flex', alignItems: 'center' }) }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={compact ? 1.5 : 2} alignItems={{ md: 'center' }} sx={{ width: '100%' }}>
        <Box sx={{
          flexShrink: 0, width: compact ? 36 : 44, height: compact ? 36 : 44, borderRadius: compact ? '10px' : '11px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#e8f4fd',
        }}>
          <CloudSyncIcon sx={{ color: '#108ae6' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h3" sx={compact ? { fontSize: "inherit", fontWeight: 700 } : undefined}>uKnowva data</Typography>
            {isLoading ? <Skeleton width={120} /> : data?.connected ? (
              <Chip size="small" color="success" variant="outlined"
                label={`Connected · ${(data.instance ?? '').replace(/^https?:\/\//, '')}`} />
            ) : (
              <Chip size="small" color="warning" variant="outlined" label="Not connected" />
            )}
            {last && <RunStatusChip status={last.status} />}
            {!!data?.needs_mapping && (
              <Chip size="small" color="warning" label={`${data.needs_mapping} need column mapping`} />
            )}
            {dupeCount > 0 && (
              <Chip size="small" color="warning" clickable onClick={() => setDupesOpen(true)}
                label={`${dupeCount} duplicate employee${dupeCount === 1 ? '' : 's'}`} />
            )}
            {data && data.connected && !data.employee_field_map_confirmed && (
              <Chip size="small" color="warning" variant="outlined" label="Employee fields not mapped" />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: compact ? 0.25 : 0.5 }}>
            {running
              ? 'A sync is running. Shifts, leave types, masters and employees are being fetched.'
              : last
                ? `Last sync ${fmtWhen(last.finished_at || last.started_at)}: ${last.summary ?? ''}`
                : data?.connected
                  ? 'Fetch shifts, leave types, other masters, branding and new APIs from uKnowva into payroll.'
                  : (data?.reason ?? 'Connect this workspace to uKnowva to fetch its masters.')}
          </Typography>
          {daily && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {daily.is_enabled
                ? `Daily sync on: ${daily.run_at} ${daily.timezone}, re-reading the last ${daily.lookback_days} day(s)`
                  + (daily.next_run_at ? `; next ${fmtWhen(daily.next_run_at)}` : '')
                : 'Daily sync is off. Attendance and punches arrive only when someone syncs.'}
            </Typography>
          )}
          {running && <LinearProgress sx={{ mt: 1, borderRadius: 2 }} />}
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {daily && (
            <Button variant="outlined" startIcon={<ScheduleIcon />} onClick={() => setScheduleOpen(true)}>
              Schedule
            </Button>
          )}
          <Button variant={running ? 'outlined' : 'contained'} startIcon={<CloudSyncIcon />}
            onClick={() => { requestDiscovery(); setFollowRun(null); setOpen(true); }} disabled={isLoading}>
            {running ? 'View progress' : 'Sync new data'}
          </Button>
        </Stack>
      </Stack>
      {open && (
        <HrmsSyncDialog open={open} onClose={() => setOpen(false)} canEdit={canEdit}
          initialRunId={followRun ?? data?.active_run_id ?? null} />
      )}
      {dupesOpen && (
        <DuplicatesDialog open={dupesOpen} onClose={() => setDupesOpen(false)} canEdit={canEdit} />
      )}
      {scheduleOpen && daily && schedules.data && (
        <SyncScheduleDialog open={scheduleOpen} data={schedules.data} schedule={daily} canEdit={canEdit}
          onClose={() => setScheduleOpen(false)}
          onStarted={(runId) => { setScheduleOpen(false); setFollowRun(runId); setOpen(true); }} />
      )}
    </Paper>
  );
}
